import { format } from 'd3-format';
import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import noop from 'lodash-es/noop';
import React, {
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { LookBackYearRange } from '../../graphQL/types/shared';
import { yearRangeEnumToNumeric } from '../../graphQL/Utils';
import { getLinearScale } from '../../NonConstantUtils';
import { formatPCI} from '../../numberFormatters';
import { formatECI } from '../../numberFormatters';
import GraphLoading from '../../sharedComponents/GraphLoading';
import {
  ITooltipDatum,
} from '../../tree/hoverTooltips/otherTypes';
import Tooltips from '../../tree/hoverTooltips/TooltipsContainer';
import useDOMElemSizeMonitoringHook from '../../tree/useDOMElemSizeMonitoringHook';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import { numXTicks, numYTicks } from './appearance';
import {
  BottomLegendNegative,
  BottomLegendPositive,
  getBottomLegendContainer,
  getLeftLegendContainer,
  getXAxisLabelContainer,
  getYAxisLabelContainer,
  LeftLegendItemNegative,
  LeftLegendItemPositive,
} from './axisElementsSharedStyles';
import Canvas from './Canvas';
import {
  ChartContainer as ChartContainerBase,
  TopLabelContainer as TopLabelContainerBase,
  XLegendContainer,
  XxisLabelContainer as XAxisLabelContainerBase,
  YAxisLabelContainer as YAxisLabelContainerBase,
  YLegendContainer as LeftLegendContainerBase,
  YTicksContainer as YTicksContainerBase,
} from './Grid';
import {
  ChartLine, ChartNode, LineStyle,
} from './otherTypes';

//#region Styling
const ChartContainer = ChartContainerBase;
const TooltipsContainer = styled(ChartContainerBase)`
  pointer-events: none;
  position: relative;
`;
const LeftLegendContainer = getLeftLegendContainer(LeftLegendContainerBase);
const BottomLegendContainerBase = getBottomLegendContainer(XLegendContainer);
const YAxisLabelContainer = getYAxisLabelContainer(YAxisLabelContainerBase);
const XAxisLabelContainer = getXAxisLabelContainer(XAxisLabelContainerBase);
const BottomLegendContainer = styled(BottomLegendContainerBase)`
  align-items: flex-start;
`;
const ticksContainerStyle = css`
  position: relative;
`;
const xTickHeight = 6; // in px
const XTick = styled.div`
  left: var(--translation);
  top: 50%;
  transform: translateX(-50%);
  position: absolute;
  padding-top: ${xTickHeight}px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 1px;
    height: ${xTickHeight}px;
    background-color: black;
    display: block;
  }
`;
const XTicksOuterContainer = styled(ChartContainer)`
  position: relative;
  pointer-events: none;
`;

export const labelFontSize = '0.8125rem';
const labelFontColor = '#666';

const XTicksInnerContainer = styled.div`
  position: absolute;
  top: var(--translation);
  width: 100%;
  font-size: ${labelFontSize};
  color: ${labelFontColor};
`;
const YTicksContainer = styled(YTicksContainerBase)`
  ${ticksContainerStyle}
`;
const YTick = styled.div`
  top: var(--translation);
  transform: translateY(-50%);
  position: absolute;
  font-size: ${labelFontSize};
  color: ${labelFontColor};
  width: 100%;
  text-align: right;
  padding-right: 6px;
`;
const TopLabelContainer = styled(TopLabelContainerBase)`
  position: relative;
`;
const TopLabel = styled.div`
  position: absolute;
  left: var(--translation);
  transform: translateX(-50%);
  font-size: ${labelFontSize};
  color: #5065a8;
  white-space: nowrap;
`;
//#endregion

const formatYAxisTickLabels = (input: number) => (input === 0) ? '   0' : format(' >-4.0%')(input);

interface Props {
  nodes: ChartNode[];
  maxComplexity: number;
  minComplexity: number;
  maxGrowth: number;
  minGrowth: number;
  eci: number | null;
  getFluentString: GetString;
  yearRange: LookBackYearRange;
  year: number;
  tooltipMap: Map<string, ITooltipDatum>;
  // in milliseconds:
  hoverTransitionDuration: number;
  dataSetTransitionDuration: number;
  fluentBundle: FluentBundle;
  getZIndex: (value: string) => number;
  promoteXAxisLabelContainerZIndex: () => void;
  restoreZIndex: () => void;
}

export default (props: Props) => {
  const {
    nodes,
    minComplexity, maxComplexity,
    minGrowth, maxGrowth, eci,
    getFluentString, yearRange, hoverTransitionDuration, dataSetTransitionDuration,
    tooltipMap, fluentBundle, getZIndex, promoteXAxisLabelContainerZIndex,
    restoreZIndex, year,
  } = props;
  const chartRootElRef = useRef<HTMLDivElement | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | undefined>(undefined);

  const resetHoveredNode = () => setHoveredNode(undefined);

  const {size} = useDOMElemSizeMonitoringHook({
    elemRef: chartRootElRef,
  });

  let chart: React.ReactElement<any> | null;
  let xTicksContainer: React.ReactElement<any> | null;
  let yTicksContainer: React.ReactElement<any> | null;
  let topLabelContainer: React.ReactElement<any> | null;
  if (size !== undefined) {
    const {width, height} = size;
    const xMaxVal = (eci !== null && eci > maxComplexity) ? eci : maxComplexity;
    const xScale = getLinearScale([minComplexity, xMaxVal], [0, width]);
    const yScale = getLinearScale([minGrowth, maxGrowth], [height, 0]);
    const xTickValues = xScale.ticks(numXTicks);
    const yTickValues = yScale.ticks(numYTicks);

    const buffer = 6;
    const yCoordOfXAxis = yScale(0);
    let xTicksContainerTranslation: number;
    if (yCoordOfXAxis < 0) {
      xTicksContainerTranslation = 0;
    } else if (yCoordOfXAxis > height) {
      xTicksContainerTranslation = height - buffer;
    } else {
      xTicksContainerTranslation = height - buffer;
    }
    const xTicks = xTickValues.map(val => (
      <XTick style={{'--translation': `${xScale(val)}px`}} key={`x-tick-${val}`}>
        {formatPCI(val)}
      </XTick>
    ));
    xTicksContainer = (
      <XTicksOuterContainer  style={{zIndex: getZIndex('xTicksOuterContainer')}}>
        <XTicksInnerContainer style={{'--translation': `${xTicksContainerTranslation}px`}}>
          {xTicks}
        </XTicksInnerContainer>
      </XTicksOuterContainer>
    );
    const yTicks = yTickValues.map(val => (
      <YTick style={{'--translation': `${yScale(val)}px`}} key={`y-tick-${val}`}>
        {formatYAxisTickLabels(val)}
      </YTick>
    ));
    yTicksContainer = (
      <YTicksContainer  style={{zIndex: getZIndex('yTicksContainer')}}>
        {yTicks}
      </YTicksContainer>
    );

    let lines: ChartLine[] = [];
    const standardThickness = 0.5;
    const longDashThickness = 3;
    const axisThickness = 2;

    const horizontalGridLines: ChartLine[] = [];
    for (const value of yTickValues) {
      // Not include zero because the x-axis will be rendered as a solid line
      if (value !== 0) {
        const yCoord = yScale(value);
        const line: ChartLine = {
          id: `horizontal-grid-line-${Math.round(yCoord)}`,
          x0: 0, y0: yCoord,
          x1: width, y1: yCoord,
          style: LineStyle.ShortDash,
          thickness: standardThickness,
        };
        horizontalGridLines.push(line);
      }
    }
    const verticalGridLines: ChartLine[] = [];
    for (const value of xTickValues) {
      // Not include zero because the x-axis will be rendered as a solid line
      if (value !== 0) {
        const xCoord = xScale(value);
        const line: ChartLine = {
          id: `horizontal-grid-line-${Math.round(xCoord)}`,
          x0: xCoord, y0: 0,
          x1: xCoord, y1: height,
          style: LineStyle.ShortDash,
          thickness: standardThickness,
        };
        verticalGridLines.push(line);
      }
    }
    lines = [...lines, ...horizontalGridLines, ...verticalGridLines];

    const xCoordOfYAxis = xScale(0);
    if (xCoordOfYAxis > 0 && xCoordOfYAxis < width) {
      // Only draw the y axis if the y axis is within the frame:
      const yAxisLine: ChartLine = {
        id: `y-axis-${Math.round(xCoordOfYAxis)}`,
        x0: xCoordOfYAxis, y0: 0,
        x1: xCoordOfYAxis, y1: height,
        style: LineStyle.Solid,
        thickness: axisThickness,
      };
      lines = [...lines, yAxisLine];
    }

    if (yCoordOfXAxis > 0 && yCoordOfXAxis < height) {
      const xAxisLine: ChartLine = {
        id: `x-axis-${Math.round(yCoordOfXAxis)}`,
        x0: 0, y0: yCoordOfXAxis,
        x1: width, y1: yCoordOfXAxis,
        style: LineStyle.Solid,
        thickness: axisThickness,
      };
      lines = [...lines, xAxisLine];
    }

    if (eci === null) {
      topLabelContainer = null;
    } else {
      const xCoordOfEci = xScale(eci);
      const eciLine: ChartLine = {
        id: `average-complexity-${Math.round(xCoordOfEci)}`,
        x0: xCoordOfEci, y0: 0,
        x1: xCoordOfEci, y1: height,
        style: LineStyle.LongDash,
        thickness: longDashThickness,
      };
      lines = [...lines, eciLine];

      const topLabelPhrase = getFluentString(
        'export-growth-dynamics-graph-top-label', {
          'avg-complexity': formatECI(eci),
          'year': year.toString(),
        },
      );
      topLabelContainer = (
        <TopLabelContainer style={{zIndex: getZIndex('topLabelContainer')}}>
          <TopLabel style={{'--translation': `${xCoordOfEci}px`}}>{topLabelPhrase}</TopLabel>
        </TopLabelContainer>
      );
    }

    chart = (
      <Canvas
        nodes={nodes}
        lines={lines}
        hoveredNode={hoveredNode}
        width={width}
        height={height}
        shortDashLength={2}
        longDashLength={6}
        lineColor={[0, 0, 0, 0.6]}
        longDashColor={[0.31, 0.39, 0.65, 1]}
        hoverTransitionDuration={hoverTransitionDuration}
        dataSetTransitionDuration={dataSetTransitionDuration}
        unhoveredNodeColor={[0.2, 0.2, 0.2]}
        onNodeMouseEnter={setHoveredNode}
        onNodeMouseLeave={resetHoveredNode}
        maxNumLabels={2}
      />
    );
  } else {
    chart = (
      <GraphLoading/>
    );
    xTicksContainer = null;
    yTicksContainer = null;
    topLabelContainer = null;
  }

  const numYears = yearRangeEnumToNumeric(yearRange);
  const leftLegendPositve = getFluentString(
    'export-growth-dynamics-graph-marginal-legend-y-positive',
    {'year-range': numYears},
  );
  const leftLegendNegative = getFluentString(
    'export-growth-dynamics-graph-marginal-legend-y-negative',
    {'year-range': numYears},
  );
  const bottomLegendPositive = getFluentString(
    'export-growth-dynamics-graph-marginal-legend-x-positive',
  );
  const bottomLegendNegative = getFluentString(
    'export-growth-dynamics-graph-marginal-legend-x-negative',
  );
  const xAxisLabelArgs = {
    productComplexityGlossaryTerm: (
      <GlossaryTerm
        type={TooltipType.Standard}
        term={getFluentString('glossary-product-complexity-term-name')}
        explanation={getFluentString('glossary-product-complexity-term-definition')}
        citation={undefined}
        align={TooltipAlign.Standard}
        attachment={null}
      />
    ),
  };
  const xAxisLabel = (
    <Localized
      bundle={fluentBundle}
      messageId='export-growth-dynamics-graph-marginal-x-label'
      messageArgs={xAxisLabelArgs}
    >
      <div />
    </Localized>
  );
  const yAxisLabel = getFluentString(
    'export-growth-dynamics-graph-marginal-y-label', {
      'start-year': (year - numYears).toString(),
      'end-year': year.toString(),
    },
  );

  return (
    <>
      <LeftLegendContainer style={{zIndex: getZIndex('leftLegendContainer')}}>
        <LeftLegendItemNegative>{leftLegendNegative}</LeftLegendItemNegative>
        <LeftLegendItemPositive>{leftLegendPositve}</LeftLegendItemPositive>
      </LeftLegendContainer>
      <BottomLegendContainer style={{zIndex: getZIndex('bottomLegendContainer')}}>
        <BottomLegendNegative> {bottomLegendNegative} </BottomLegendNegative>
        <BottomLegendPositive> {bottomLegendPositive} </BottomLegendPositive>
      </BottomLegendContainer>
      {topLabelContainer}
      <YAxisLabelContainer style={{zIndex: getZIndex('yAxisLabelContainer')}}>
        {yAxisLabel}
      </YAxisLabelContainer>
      <XAxisLabelContainer
        style={{zIndex: getZIndex('xAxisLabelContainer')}}
        onMouseEnter={promoteXAxisLabelContainerZIndex}
        onMouseLeave={restoreZIndex}
      >
        {xAxisLabel}
      </XAxisLabelContainer>
      <ChartContainer
        ref={chartRootElRef}
        style={{zIndex: getZIndex('chartContainer')}}
       >
        {chart}
      </ChartContainer>
      {yTicksContainer}
      {xTicksContainer}
      <TooltipsContainer style={{zIndex: getZIndex('tooltipsContainer')}}>
        <Tooltips
          hovered={hoveredNode}
          highlighted={undefined}
          tooltipMap={tooltipMap}
          clearHighlightTooltip={noop}
        />
      </TooltipsContainer>
    </>
  );
};
